import {
  Button,
  Container,
  Form,
  Frequency,
  Input,
  RadioGroup,
  Stack,
  Text,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { AccountInfo } from "library/hooks/types";
import { formatAmount } from "library/moneyUtils";
import { yesNoOptions } from "library/sharedOptions";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard } from "../CreditCard/CreditCard";
import { Separator } from "../Separator/Separator";
import { Cancel } from "./Cancel";
import { CancelLink } from "./components/CancelLink";
import { ActionType, ScreenProps } from "./types";
import { getTotalExpenses } from "./utils";

export const IncomeAndExpenses = ({ state, dispatch }: ScreenProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();

  const accountInfo = state.accountInfo as AccountInfo;
  const initialValues = {
    income: state.income,
    monthlyExpenses: state.monthlyExpenses,
    additionalDebts: state.additionalDebts,
  };
  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length === 0) {
      dispatch({
        type: ActionType.INCOME_AND_EXPENSES_SUBMITTED,
        income: values.income,
        monthlyExpenses: {
          ...values.monthlyExpenses,
          totalExpenses: getTotalExpenses(
            values.monthlyExpenses.expenses,
            errors.monthlyExpenses?.expenses,
          ),
        },
        additionalDebts: values.additionalDebts,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values, errors } }) => {
        const totalExpenses = getTotalExpenses(
          values.monthlyExpenses.expenses,
          errors.monthlyExpenses?.expenses,
        );

        return (
          <>
            <Stack gap="8" flatten>
              <Container bg="grey.t05" padding="4">
                <CreditCard
                  cardName={accountInfo.cardName}
                  cardEndsWith={accountInfo.cardEndsWith}
                />
              </Container>
              <Stack gap="4">
                <Text>
                  <strong>Step 2 of 3</strong>
                </Text>
                <Text as="h3" textStyle="heading4">
                  Your income and expenses
                </Text>
              </Stack>
              <Separator />
              <Text as="h4" textStyle="heading5" color="primary.blue.t100">
                Income
              </Text>
              <Container width="100%" width-sm="400">
                <Frequency
                  name="income.netIncome"
                  mode="select"
                  amountMaxLength="6"
                  label="Net income (after tax)"
                  amountPrefix="$"
                  annually={false}
                  quarterly={false}
                />
              </Container>
              <Container width="100%" width-sm="400">
                <RadioGroup
                  name="income.otherSourcesOfIncome"
                  label="Do you have any other sources of income?"
                  options={yesNoOptions}
                  helpText="You need to include all of your income. This could include investment income, interest, share dividends, rent & Government benefits (e.g. Family tax benefit part A and B, Carer Allowance, Pensions, Partner Allowance and Parenting payment)"
                />
              </Container>
              {values.income.otherSourcesOfIncome === "yes" && (
                <Container width="100%" width-sm="400">
                  <Frequency
                    name="income.otherIncome"
                    mode="select"
                    label="Other income (after tax)"
                    amountPrefix="$"
                    amountMaxLength="6"
                    annually={false}
                    quarterly={false}
                  />
                </Container>
              )}
              <Separator />
              <Text as="h4" textStyle="heading5" color="primary.blue.t100">
                Monthly expenses
              </Text>
              <Container width="100%" width-sm="400">
                <RadioGroup
                  name="monthlyExpenses.shareLivingExpenses"
                  label="Do you share your living expenses?"
                  options={yesNoOptions}
                />
              </Container>
              <Stack gap="2">
                <Text textStyle="heading6">
                  We need to know a little more about your expenses
                </Text>
                <Text>
                  Only include your share of the living expenses each month. If
                  one of the categories doesn't apply to you, please enter 0.
                </Text>
              </Stack>
              <Container width="100%" width-sm="400">
                <Input
                  name="monthlyExpenses.expenses.lifestyle"
                  label="Eating out, groceries and entertainment"
                  variant="numeric"
                  maxLength="6"
                  prefix="$"
                  suffix="per month"
                  helpText="e.g. supermarket shop, holidays and pet expenses"
                />
              </Container>
              <Container width="100%" width-sm="400">
                <Input
                  name="monthlyExpenses.expenses.education"
                  label="Education and childcare"
                  variant="numeric"
                  maxLength="6"
                  prefix="$"
                  suffix="per month"
                  helpText="E.g. tuition fees (primary and secondary), uniforms, books and excursions etc."
                />
              </Container>
              <Container width="100%" width-sm="400">
                <Input
                  name="monthlyExpenses.expenses.transport"
                  label="Transport"
                  variant="numeric"
                  maxLength="6"
                  prefix="$"
                  suffix="per month"
                  helpText="e.g. public transport, car registration, servicing, petrol, parking and road tolls"
                />
              </Container>
              <Container width="100%" width-sm="400">
                <Input
                  name="monthlyExpenses.expenses.insurances"
                  label="Insurances"
                  variant="numeric"
                  prefix="$"
                  maxLength="6"
                  suffix="per month"
                  helpText="e.g. health, car, home and life insurance"
                />
              </Container>
              <Container width="100%" width-sm="400">
                <Input
                  name="monthlyExpenses.expenses.utilities"
                  label="Utilities"
                  variant="numeric"
                  prefix="$"
                  maxLength="6"
                  suffix="per month"
                  helpText="e.g. electricity, gas, phone, internet, streaming and pay TV bills"
                />
              </Container>
              <Container width="100%" width-sm="400">
                <Input
                  name="monthlyExpenses.expenses.personal"
                  label="Personal and other expenses"
                  variant="numeric"
                  maxLength="6"
                  prefix="$"
                  suffix="per month"
                  helpText="e.g. clothing, haircuts, cosmetics, medical, fitness, personal care for yourself and your dependants"
                />
              </Container>
              <Text>
                <strong>
                  {`Your total monthly living expenses: ${
                    totalExpenses === null ? "–" : formatAmount(totalExpenses)
                  }`}
                </strong>
              </Text>
              <Separator />
              <Text as="h4" textStyle="heading5" color="primary.blue.t100">
                Additional debts
              </Text>
              <Container width="100%" width-sm="400">
                <RadioGroup
                  name="additionalDebts.haveAdditionalDebts"
                  label="Do you have any personal loans, credit cards or home loans you haven't already told us about?"
                  options={yesNoOptions}
                />
              </Container>
              {values.additionalDebts.haveAdditionalDebts === "yes" && (
                <>
                  <Text textStyle="heading6">
                    Your share of additional debts
                  </Text>
                  <Container width="100%" width-sm="400">
                    <Input
                      name="additionalDebts.loanRepayments"
                      label="Loan repayments"
                      variant="numeric"
                      prefix="$"
                      maxLength="6"
                      suffix="per month"
                      helpText="Your share of personal, investment mortgages or car loan repayments, etc."
                    />
                  </Container>
                  <Text textStyle="heading6">Your current credit card(s)</Text>
                  <Container width="100%" width-sm="400">
                    <Input
                      name="additionalDebts.totalCreditLimit"
                      label="Total credit limit"
                      variant="numeric"
                      prefix="$"
                      maxLength="6"
                      helpText={`Your total credit limit across all of your other cards (not including your ${accountInfo.cardName})`}
                    />
                  </Container>
                  <Container width="100%" width-sm="400">
                    <Input
                      name="additionalDebts.totalOwing"
                      label="Total amounts owing"
                      variant="numeric"
                      prefix="$"
                      maxLength="6"
                      helpText={`All of your other cards (not including your ${accountInfo.cardName})`}
                    />
                  </Container>
                </>
              )}
              <Container width="100%" width-sm="400">
                <FormActions>
                  <FormActions.Primary>
                    <Button type="submit">Next</Button>
                  </FormActions.Primary>
                  <FormActions.Secondary>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        dispatch({
                          type: ActionType.INCOME_AND_EXPENSES_BACK_CLICKED,
                          income: values.income,
                          monthlyExpenses: values.monthlyExpenses,
                          additionalDebts: values.additionalDebts,
                        });
                      }}
                    >
                      Back
                    </Button>
                  </FormActions.Secondary>
                </FormActions>
              </Container>
              <CancelLink onClick={() => setIsModalOpen(true)} />
            </Stack>
            <Cancel
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onConfirm={() => {
                navigate("/manage-account");
              }}
            />
          </>
        );
      }}
    </Form>
  );
};
